import dayjs from '@/lib/dayjs'
import { getDeliveryRouteSelectionId, getSafeDayjs } from './delivery-share.utils'

export const MAP_BUCKETS = [
  { id: 'pastDue', label: 'Delivery / Pickup Date Passed' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'future', label: 'Future' },
]

export const SINGLE_LOCATION_ZOOM = 11
export const FITTED_MAP_PADDING = 48
export const ORDERED_ROUTE_PREVIEW_DEBOUNCE_MS = 300
export const MARKER_AVAILABLE_COLOR = '#52525b'
export const MARKER_ACTIVE_ROUTE_COLOR = '#0ea5e9'
export const MARKER_SELECTED_COLOR = MARKER_ACTIVE_ROUTE_COLOR
export const MARKER_DELIVERED_COLOR = '#16a34a'
export const MARKER_MISSED_COLOR = '#fac547'
export const MARKER_MISSED_GLYPH_COLOR = '#442700'
export const MARKER_ORIGIN_COLOR = '#7dd3fc'
export const MARKER_ORIGIN_GLYPH_COLOR = '#075985'
export const MARKER_ORIGIN_BORDER_COLOR = '#ffffff'
export const MARKER_DRIVER_COLOR = '#2563eb'
export const MARKER_DRIVER_GLYPH_COLOR = '#ffffff'
export const MARKER_DRIVER_BORDER_COLOR = '#ffffff'
export const MARKER_DEFAULT_SHADOW = '0 8px 18px rgb(15 23 42 / 0.25)'
export const MARKER_HIGHLIGHT_SHADOW =
  '0 0 0 4px rgb(14 165 233 / 0.32), 0 10px 22px rgb(15 23 42 / 0.28)'
export const DELIVERY_ROUTE_SHOW_ROUTE_STORAGE_KEY = 'delivery-route-builder.show-route'
export const DELIVERY_ROUTE_MAP_STYLES = [
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.business',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.attraction',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.medical',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.school',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.sports_complex',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
]

export function getStoredShowOptimizedRoutePreference() {
  if (typeof globalThis.localStorage === 'undefined') {
    return true
  }

  try {
    const storedValue = globalThis.localStorage.getItem(DELIVERY_ROUTE_SHOW_ROUTE_STORAGE_KEY)

    if (storedValue === 'false') {
      return false
    }

    if (storedValue === 'true') {
      return true
    }
  } catch {
    return true
  }

  return true
}

export function rememberShowOptimizedRoutePreference(showRoute) {
  if (typeof globalThis.localStorage === 'undefined') {
    return
  }

  try {
    globalThis.localStorage.setItem(
      DELIVERY_ROUTE_SHOW_ROUTE_STORAGE_KEY,
      showRoute ? 'true' : 'false'
    )
  } catch {
    // Local memory is a convenience only; failing to persist should not block route editing.
  }
}

export function getDeliveryRouteMapBucket(order, today = dayjs().format('YYYY-MM-DD')) {
  const deliveryDate = getSafeDayjs(order?.fulfillment_due_date)
  const todayDate = getSafeDayjs(today) ?? dayjs()
  const tomorrowDate = todayDate.add(1, 'day')

  if (!deliveryDate) {
    return 'future'
  }

  if (deliveryDate.isBefore(todayDate, 'day')) {
    return 'pastDue'
  }

  if (deliveryDate.isSame(todayDate, 'day')) {
    return 'today'
  }

  if (deliveryDate.isSame(tomorrowDate, 'day')) {
    return 'tomorrow'
  }

  return 'future'
}

export function getDeliveryRouteMapDefaultBuckets(
  orders = [],
  today = dayjs().format('YYYY-MM-DD')
) {
  return {
    pastDue: orders.some((order) => getDeliveryRouteMapBucket(order, today) === 'pastDue'),
    today: true,
    tomorrow: false,
    future: false,
  }
}

export function getDeliveryRouteMapBucketsForOrders(
  orders = [],
  today = dayjs().format('YYYY-MM-DD')
) {
  const buckets = {
    pastDue: false,
    today: false,
    tomorrow: false,
    future: false,
  }

  orders.forEach((order) => {
    const bucket = getDeliveryRouteMapBucket(order, today)

    if (bucket) {
      buckets[bucket] = true
    }
  })

  return Object.values(buckets).some(Boolean)
    ? buckets
    : {
        pastDue: false,
        today: true,
        tomorrow: false,
        future: false,
      }
}

export function getDeliveryRouteMapFilteredOrders(
  orders = [],
  enabledBuckets = {},
  today = dayjs().format('YYYY-MM-DD')
) {
  return orders.filter((order) => enabledBuckets[getDeliveryRouteMapBucket(order, today)])
}

export function getDeliveryRouteMapFilteredOrdersWithPinnedOrders(
  orders = [],
  enabledBuckets = {},
  pinnedOrderIds = [],
  today = dayjs().format('YYYY-MM-DD')
) {
  const pinnedOrderIdSet = new Set(pinnedOrderIds.map(String))

  return orders.filter(
    (order) =>
      pinnedOrderIdSet.has(String(getDeliveryRouteSelectionId(order))) ||
      enabledBuckets[getDeliveryRouteMapBucket(order, today)]
  )
}

export function getDeliveryRouteMapHiddenSelectedOrderIds(
  orders = [],
  selectedOrderIds = [],
  enabledBuckets = {},
  today = dayjs().format('YYYY-MM-DD')
) {
  const visibleOrderIds = new Set(
    getDeliveryRouteMapFilteredOrders(orders, enabledBuckets, today).map(
      getDeliveryRouteSelectionId
    )
  )

  return selectedOrderIds.filter((orderId) => !visibleOrderIds.has(orderId))
}

export function getDeliveryRouteMapAddress(order) {
  return [
    order?.delivery_address?.line1,
    order?.delivery_address?.line2,
    order?.delivery_address?.city,
    order?.delivery_address?.state,
    order?.delivery_address?.postal_code,
    order?.delivery_address?.country,
  ]
    .filter(Boolean)
    .join(', ')
}

export function getDeliveryRouteMapPosition(order, geocodedPositions = {}) {
  const rawLat = order?.delivery_address?.lat
  const rawLng = order?.delivery_address?.lng
  const hasStoredPosition =
    rawLat !== null &&
    typeof rawLat !== 'undefined' &&
    rawLat !== '' &&
    rawLng !== null &&
    typeof rawLng !== 'undefined' &&
    rawLng !== ''
  const lat = Number(rawLat)
  const lng = Number(rawLng)

  if (hasStoredPosition && Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng }
  }

  return geocodedPositions[getDeliveryRouteSelectionId(order)] ?? null
}

export function getMapAddressPosition(address) {
  const rawLat = address?.lat
  const rawLng = address?.lng
  const hasPosition =
    rawLat !== null &&
    typeof rawLat !== 'undefined' &&
    rawLat !== '' &&
    rawLng !== null &&
    typeof rawLng !== 'undefined' &&
    rawLng !== ''
  const lat = Number(rawLat)
  const lng = Number(rawLng)

  return hasPosition && Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

export function formatMapAddress(address) {
  return [
    address?.line1,
    address?.line2,
    address?.city,
    address?.state,
    address?.postal_code,
    address?.country,
  ]
    .filter(Boolean)
    .join(', ')
}

export function getDeliveryRouteMapLocationPoint(location) {
  return getMapAddressPosition(location?.address) ?? (formatMapAddress(location?.address) || null)
}

export function getDeliveryRouteMapLocationKey(location) {
  const address = formatMapAddress(location?.address)

  if (location?.id !== null && typeof location?.id !== 'undefined' && location?.id !== '') {
    return ['location', location.id, address].filter(Boolean).join(':')
  }

  return address ? `address:${address}` : null
}

export function getDeliveryRouteMapLocations(orders = []) {
  const locationsByKey = new Map()

  orders.forEach((order) => {
    const location = order?.location
    const key = getDeliveryRouteMapLocationKey(location)

    if (location && key && !locationsByKey.has(key)) {
      locationsByKey.set(key, location)
    }
  })

  return Array.from(locationsByKey.values())
}

export function getDeliveryRouteMapLocationLabel(location) {
  return location?.address?.name || location?.name || 'Florist'
}

export function getDeliveryRouteMapLocationRouteSignature(location) {
  const address = location?.address

  return [
    location?.id ?? '',
    location?.name ?? '',
    address?.line1 ?? '',
    address?.line2 ?? '',
    address?.city ?? '',
    address?.state ?? '',
    address?.postal_code ?? '',
    address?.country ?? '',
    address?.lat ?? '',
    address?.lng ?? '',
  ].join('/')
}

export function getDeliveryRouteMapLocationMarkerPosition(
  location,
  geocodedLocationPositions = {}
) {
  return (
    getMapAddressPosition(location?.address) ??
    geocodedLocationPositions[getDeliveryRouteMapLocationKey(location)] ??
    null
  )
}

export function getDeliveryRouteMapLocationMarkerTitle(location) {
  return `${getDeliveryRouteMapLocationLabel(location)} - Florist location`
}

export function getDeliveryRouteMapDriverLocationPosition(driverLocation) {
  const rawLat = driverLocation?.lat
  const rawLng = driverLocation?.lng
  const hasPosition =
    rawLat !== null &&
    typeof rawLat !== 'undefined' &&
    rawLat !== '' &&
    rawLng !== null &&
    typeof rawLng !== 'undefined' &&
    rawLng !== ''
  const lat = Number(rawLat)
  const lng = Number(rawLng)

  return hasPosition && Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}
